import { InjectionToken, type Type, type Injector } from '@angular/core';

export interface EntityDetailVisibility {
  mode: 'all' | 'any';
  permissions: readonly string[];
}

/** Serializable detail-page structure safe to supply as assistant UI context. */
export interface EntityDetailMetadata {
  entity: string;
  route: string;
  titleKey: string;
  sections: EntityDetailSectionMetadata[];
  actions: EntityDetailActionMetadata[];
  visibleWhen?: EntityDetailVisibility;
}

export interface EntityDetailSectionMetadata {
  id: string;
  titleKey: string;
  visibleWhen?: EntityDetailVisibility;
  requiresEntityId?: boolean;
}

export interface EntityDetailActionMetadata {
  id: string;
  labelKey: string;
  sectionId?: string;
  visibleWhen?: EntityDetailVisibility;
  requiresEntityId?: boolean;
  intent: 'navigate' | 'mutation';
}

/** App registration may defer loading a metadata-only lazy UI library. */
export type EntityDetailMetadataSource = EntityDetailMetadata | (() => Promise<readonly EntityDetailMetadata[]>);

/** Application-wide multi-provider for serializable entity-detail metadata. */
export const ENTITY_DETAIL_METADATA = new InjectionToken<EntityDetailMetadataSource>('Entity Detail Metadata');

export interface EntityEditSection {
  id: string;
  icon: string;
  titleKey: string;
  priority: number;
  component: Type<unknown>;
  /** Render component directly in the host accordion when it supplies its own accordion item(s). */
  wrapInAccordion?: boolean;
  requiresEntityId?: boolean;
  visibilityCheck?: (injector: Injector) => boolean;
  editCheck?: (injector: Injector) => boolean;
}

export function createEditSectionsToken(entityName: string): InjectionToken<EntityEditSection[]> {
  return new InjectionToken<EntityEditSection[]>(`${entityName} Edit Sections`);
}
